import { TransactionType } from '@converge/graphql';
import { Dispatch, SetStateAction } from 'react';
import { ModalStep } from '../insured-policies.detail';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';
import { ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { NewConvergeButton } from '../../../../presentation';
import { modalCommonStyle } from '../../utils';

export interface MidtermTransactionModalProps {
  onClose: () => void;
  policyUuid: string;
  effectiveDate?: string;
  maxDate?: Date;
  showClaimMessage?: boolean;
  hasOpenClaims?: boolean;
  transactionType: TransactionType;
  setOpenNextPage?: Dispatch<SetStateAction<boolean>>;
  claimsLoading?: boolean;
}

export enum ModalLabels {
  CANCEL = 'CANCEL',
  CONFIRM = 'CONFIRM',
  NEXT = 'NEXT',
  BACK = 'BACK',
  SAVE = 'SAVE',
}

type ModalStepConfig = {
  title: JSX.Element;
  content: JSX.Element;
  prevLabel: ModalLabels;
  nextLabel: ModalLabels;
  nextStep: ModalStep | null;
  height?: number;
  width?: number;
};

export type ModalConfigType = Record<string, ModalStepConfig>;

export const stepValues = (currentStep: ModalStep, step: ModalStepConfig) => {
  const title = currentStep ? step.title : null;
  const body = currentStep ? step.content : '';
  const prevButtonLabel = currentStep ? step.prevLabel : '';
  const nextButtonLabel = currentStep ? step.nextLabel : '';
  const height = step?.height;
  const width = step?.width;
  return { title, body, prevButtonLabel, nextButtonLabel, height, width };
};

export const activeClaimStepConfig = (
  content: JSX.Element,
  nextStep?: ModalStep
): ModalConfigType => {
  return {
    [ModalStep.ACTIVE_CLAIM_INFO]: {
      title: (
        <Box className="modal-title warning">
          <WarningIcon fontSize="medium" className="icon" />
          <span style={{ marginTop: '2px' }}>Active Claim Information</span>
        </Box>
      ),
      content,
      prevLabel: ModalLabels.CANCEL,
      nextLabel: ModalLabels.CONFIRM,
      nextStep: nextStep || null,
      height: 300,
    },
  };
};

export const transactionNoteStepConfig = (
  content: JSX.Element
): ModalConfigType => {
  return {
    [ModalStep.TRANSACTION_NOTE]: {
      title: <Box className="modal-title">Midterm Transaction Note</Box>,
      content,
      prevLabel: ModalLabels.BACK,
      nextLabel: ModalLabels.SAVE,
      nextStep: null,
    },
  };
};
interface MidtermTransactionModalShellProps<T extends FieldValues> {
  isOpen: boolean;
  formMethods: UseFormReturn<T>;
  onClose: () => void;
  title: ReactNode;
  body: ReactNode;
  prevButtonLabel: string;
  nextButtonLabel: string;
  onBack: () => void;
  onNext: () => void;
  isNextDisabled: boolean;
  isLoading: boolean;
  height?: number;
  width?: number;
  showClaimMessage?: boolean;
  claimsLoading?: boolean;
}

export function MidtermTransactionModal<T extends FieldValues>({
  isOpen,
  formMethods,
  onClose,
  title,
  body,
  prevButtonLabel,
  nextButtonLabel,
  onBack,
  onNext,
  isNextDisabled,
  isLoading,
  height = 400,
  width,
  showClaimMessage,
  claimsLoading = false,
}: MidtermTransactionModalShellProps<T>) {
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <FormProvider {...formMethods}>
        <Box sx={modalCommonStyle({ height, width })}>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'black',
            }}
          >
            <CloseIcon />
          </IconButton>

          {claimsLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 300,
              }}
            >
              <CircularProgress size={40} />
              <Typography sx={{ mt: 2 }}>
                Loading claims information...
              </Typography>
            </Box>
          ) : (
            <>
              <Typography
                id="modal-title"
                variant="h6"
                component="h2"
                gutterBottom
              >
                {title}
              </Typography>

              <Typography id="modal-description" sx={{ mt: 2 }}>
                {body}
              </Typography>

              <Box
                sx={{
                  position: 'absolute',
                  bottom: 27,
                  right: 27,
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <Box style={{ marginRight: '18px' }}>
                  <NewConvergeButton
                    onClick={onBack}
                    variant="outlined"
                    label={prevButtonLabel}
                  ></NewConvergeButton>
                </Box>
                <NewConvergeButton
                  onClick={onNext}
                  variant="contained"
                  disabled={isNextDisabled}
                  sx={{
                    backgroundColor: 'black',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#333',
                    },
                  }}
                  loading={isLoading}
                  label={nextButtonLabel}
                ></NewConvergeButton>
              </Box>
            </>
          )}
        </Box>
      </FormProvider>
    </Modal>
  );
}
